import { createJSONStorage, type StateStorage } from 'zustand/middleware';

/**
 * Debounced localStorage adapter for zustand persist.
 *
 * Some persisted slices change at pointer/frame rate (viewport flags, sliders).
 * A direct localStorage write per set() call would stall the UI thread, so
 * writes are coalesced into a single trailing save per store key.
 */
function createDebouncedStateStorage(delayMs = 600): StateStorage {
    const timers = new Map<string, number>();
    return {
        getItem: (name) => {
            try {
                return localStorage.getItem(name);
            } catch {
                return null;
            }
        },
        setItem: (name, value) => {
            const pending = timers.get(name);
            if (pending) window.clearTimeout(pending);
            timers.set(
                name,
                window.setTimeout(() => {
                    timers.delete(name);
                    try {
                        localStorage.setItem(name, value);
                    } catch {
                        // Storage quota / privacy mode: settings simply won't persist.
                    }
                }, delayMs)
            );
        },
        removeItem: (name) => {
            const pending = timers.get(name);
            if (pending) window.clearTimeout(pending);
            timers.delete(name);
            try {
                localStorage.removeItem(name);
            } catch {
                // ignore
            }
        },
    };
}

const debouncedLocalStorage = createDebouncedStateStorage();

/** JSON storage shared by every persisted settings store. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const settingsStorage = createJSONStorage(() => debouncedLocalStorage) as any;
